/**
 * Chute Entry Page
 * Independent bib number entry - pairs with timestamps by position
 *
 * Key concept: Bibs and Timestamps are two independent ordered lists.
 * Position #1 bib pairs with Position #1 time, etc.
 * Inserting/removing from either list automatically re-pairs.
 */

import { getTimestamps, formatTime } from '../lib/timing.js'
import { saveLocal, loadLocal, getAthleteByBib, loadRoster, queueForSync, getSelectedRace, getRaceKey, STORAGE_KEYS } from '../lib/storage.js'
import { getAthleteName } from '../lib/csv.js'
import { showConfirm } from '../lib/ui.js'
import { generateUUID } from '../lib/uuid.js'

// Storage key
const BIB_ENTRIES_KEY = 'manatiming_bib_entries'

// Local state
let bibEntries = []  // Array of { bib: number|null, skipped: boolean }
let currentBibInput = ''
let editingPosition = null  // Which position is being edited (null = adding new)
let insertAfterPosition = null  // Insert mode: after which position (0 = at start)
let keyboardHandler = null

/**
 * Initialize chute page
 */
/**
 * Initialize chute page (reset state)
 */
export function initChutePage() {
  loadChuteData()
  currentBibInput = ''
  editingPosition = null
  insertAfterPosition = null
}

/**
 * Load chute data without resetting state
 */
function loadChuteData() {
  const selectedRace = getSelectedRace()
  bibEntries = loadLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace?.id)) || []
}

/**
 * Save bib entries
 */
function saveBibEntries() {
  const selectedRace = getSelectedRace()
  saveLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace?.id), bibEntries)
}

/**
 * Check if a bib number is a duplicate (already entered)
 */
function isDuplicateBib(bib) {
  if (!bib) return false
  const bibNum = parseInt(bib, 10)
  if (isNaN(bibNum) || bibNum === 0) return false

  return bibEntries.some((entry, idx) => {
    // Skip the entry being edited
    if (editingPosition !== null && idx === editingPosition - 1) return false
    return entry.bib === bibNum
  })
}

/**
 * Get paired results (bib + time by position)
 */
function getPairedResults() {
  const timestamps = getTimestamps()
  const results = []

  const maxLen = Math.max(bibEntries.length, timestamps.length)

  for (let i = 0; i < maxLen; i++) {
    const entry = bibEntries[i] || null
    const timestamp = timestamps[i] || null
    const athlete = entry?.bib ? getAthleteByBib(entry.bib) : null

    results.push({
      position: i + 1,
      bib: entry?.bib || null,
      skipped: entry?.skipped || false,
      unknown: entry?.unknown || false,
      time: timestamp?.netTime || null,
      athlete,
      hasBib: !!entry,
      hasTime: !!timestamp
    })
  }

  return results
}

/**
 * Render the Chute page
 */
export function renderChutePage(container, onNavigate) {
  loadChuteData()
  const results = getPairedResults()
  const timestamps = getTimestamps()
  const roster = loadRoster()
  const hasRoster = roster.length > 0

  // Determine mode
  const isEditing = editingPosition !== null
  const isInserting = insertAfterPosition !== null
  const inputMode = isEditing ? 'edit' : isInserting ? 'insert' : 'add'
  const inputLabel = isEditing
    ? `Edit Position #${editingPosition}`
    : isInserting
      ? `Insert after #${insertAfterPosition}`
      : `Add #${bibEntries.length + 1}`

  // Calculate time context - the time that will be assigned to this entry
  const currentPosition = isEditing ? editingPosition :
    isInserting ? insertAfterPosition + 1 :
      bibEntries.length + 1
  const prevTime = timestamps[currentPosition - 2]?.netTime || null
  const currentTime = timestamps[currentPosition - 1]?.netTime || null
  const nextTime = timestamps[currentPosition]?.netTime || null

  // Look up athlete for current input
  let matchedAthlete = null
  let duplicateWarning = false
  if (currentBibInput) {
    matchedAthlete = getAthleteByBib(parseInt(currentBibInput, 10))
    duplicateWarning = isDuplicateBib(currentBibInput)
  }

  // Time/Bib Context for Verifier
  const lastEntryIndex = bibEntries.length - 1
  const lastEntry = lastEntryIndex >= 0 ? bibEntries[lastEntryIndex] : null
  const lastTime = lastEntryIndex >= 0 ? (timestamps[lastEntryIndex]?.netTime || null) : null
  const lastAthlete = lastEntry?.bib ? getAthleteByBib(lastEntry.bib) : null

  const selectedRace = getSelectedRace()

  container.innerHTML = `
    <div class="chute-page">
      <header class="chute-header">
        <div class="chute-header-top">
          <h1>Chute Entry</h1>
          <div class="chute-stats">
            <span class="stat">${bibEntries.length} bibs</span>
            <span class="stat">${timestamps.length} times</span>
          </div>
        </div>
        ${selectedRace ? `
          <div class="chute-race-info">
            <span class="race-name">${selectedRace.name}</span>
            <span class="race-id" style="font-size:0.6rem;color:#666;">${selectedRace.id.slice(0, 8)}...</span>
          </div>
        ` : `
          <div class="chute-no-race">
            <span class="warning">No race selected!</span>
            <button class="btn-select-race" id="selectRaceBtn">Select Race</button>
          </div>
        `}
      </header>

      <div class="chute-main">
        <!-- Verifier Quick-View for the two-person chute workflow -->
        ${lastEntry ? `
          <div class="chute-verifier-card">
            <span class="verifier-header">Last Logged (Verifier View)</span>
            <div class="verifier-main">
              <span class="verifier-bib">#${lastEntry.bib || (lastEntry.unknown ? 'UNK' : '---')}</span>
              <span class="verifier-time">${lastTime !== null ? formatTime(lastTime) : '--:--.--'}</span>
            </div>
            ${lastAthlete ? `<div class="verifier-athlete">${getAthleteName(lastAthlete)} - ${lastAthlete.team || ''}</div>` : ''}
          </div>
        ` : ''}

        <!-- Time Context - Shows surrounding times -->
        <div class="chute-time-context">
          <div class="time-context-item prev">
            <span class="time-context-label">#${currentPosition - 1}</span>
            <span class="time-context-value">${prevTime !== null ? formatTime(prevTime) : '--:--.--'}</span>
          </div>
          <div class="time-context-item current">
            <span class="time-context-label">#${currentPosition}</span>
            <span class="time-context-value">${currentTime !== null ? formatTime(currentTime) : '--:--.--'}</span>
          </div>
          <div class="time-context-item next">
            <span class="time-context-label">#${currentPosition + 1}</span>
            <span class="time-context-value">${nextTime !== null ? formatTime(nextTime) : '--:--.--'}</span>
          </div>
        </div>

        <!-- Entry Section -->
        <div class="entry-section">
          <div class="input-label ${inputMode}">${inputLabel}</div>

          <div class="bib-input-container">
            <div class="bib-input ${inputMode} ${duplicateWarning ? 'duplicate' : ''}" id="bibInput">
              ${currentBibInput || '<span class="placeholder">Bib #</span>'}
            </div>
            ${duplicateWarning ? `
              <div class="athlete-match duplicate-warning">
                <span class="warning-icon">⚠</span>
                <span class="match-name">Duplicate Bib - Already Entered</span>
              </div>
            ` : matchedAthlete ? `
              <div class="athlete-match found">
                <span class="match-name">${getAthleteName(matchedAthlete)}</span>
                <span class="match-team">${matchedAthlete.team || ''}</span>
                ${currentTime !== null ? `<span class="match-time">${formatTime(currentTime)}</span>` : ''}
              </div>
            ` : currentBibInput && hasRoster ? `
              <div class="athlete-match not-found">
                <span class="match-name">Not in roster</span>
                ${currentTime !== null ? `<span class="match-time">${formatTime(currentTime)}</span>` : ''}
              </div>
            ` : currentTime !== null ? `
              <div class="athlete-match">
                <span class="match-time">Time: ${formatTime(currentTime)}</span>
              </div>
            ` : '<div class="athlete-match"></div>'}
          </div>

          <div class="entry-actions">
            ${isEditing || isInserting ? `
              <button class="btn-action btn-cancel" id="cancelBtn">Cancel</button>
            ` : ''}
            <button class="btn-action btn-unknown" id="unknownBtn">UNK</button>
            <button class="btn-action btn-skip" id="skipBtn">Skip</button>
            <button class="btn-action btn-submit" id="submitBtn" ${!currentBibInput ? 'disabled' : ''}>
              ${isEditing ? 'Save' : 'Add'}
            </button>
          </div>
        </div>

        <!-- Numeric Keypad - Mobile Optimized -->
        <div class="keypad">
          <div class="keypad-row">
            <button class="keypad-btn" data-key="1">1</button>
            <button class="keypad-btn" data-key="2">2</button>
            <button class="keypad-btn" data-key="3">3</button>
          </div>
          <div class="keypad-row">
            <button class="keypad-btn" data-key="4">4</button>
            <button class="keypad-btn" data-key="5">5</button>
            <button class="keypad-btn" data-key="6">6</button>
          </div>
          <div class="keypad-row">
            <button class="keypad-btn" data-key="7">7</button>
            <button class="keypad-btn" data-key="8">8</button>
            <button class="keypad-btn" data-key="9">9</button>
          </div>
          <div class="keypad-row">
            <button class="keypad-btn btn-clear" data-key="clear">C</button>
            <button class="keypad-btn" data-key="0">0</button>
            <button class="keypad-btn btn-back" data-key="back">⌫</button>
          </div>
          <div class="keypad-row keypad-row-enter">
            <button class="keypad-btn btn-enter" data-key="enter" ${!currentBibInput ? 'disabled' : ''}>ENTER</button>
          </div>
        </div>

        <!-- Results List -->
        <div class="results-section">
          <div class="results-header">
            <h3>Finish Order</h3>
            ${bibEntries.length > 0 ? `
              <button class="btn-small btn-danger" id="clearAllBtn">Clear All</button>
            ` : ''}
          </div>

          <div class="results-list-container">
            <!-- Insert at position 1 button -->
            ${results.length > 0 ? `
              <div class="insert-row">
                <button class="btn-insert-between" data-after="0" title="Insert at position 1">+ Insert #1</button>
              </div>
            ` : ''}

            <ul class="results-list" id="resultsList">
              ${results.map((r, idx) => {
    const isLastLogged = r.position === bibEntries.length
    const isActiveAdd = r.position === bibEntries.length + 1 && !isEditing && !isInserting
    const isEditingThis = isEditing && r.position === editingPosition

    return `
                  <li class="result-item ${r.unknown ? 'unknown' : ''} ${r.skipped ? 'skipped' : ''} ${isLastLogged ? 'last-logged' : ''} ${isActiveAdd ? 'active-recording' : ''} ${isEditingThis ? 'editing' : ''}" 
                       data-position="${r.position}">
                    <span class="result-position">#${r.position}</span>
                    <span class="result-time">${r.time !== null ? formatTime(r.time) : '--:--.--'}</span>
                    <span class="result-bib ${r.unknown ? 'unknown' : ''} ${r.skipped ? 'skipped' : ''}">${r.skipped ? 'SKIP' : r.unknown ? 'UNK' : r.bib || '---'}</span>
                    <span class="result-name">${r.athlete ? getAthleteName(r.athlete) : ''}</span>
                    <div class="result-actions">
                      <button class="btn-edit-bib" data-position="${r.position}" title="Edit bib">✎</button>
                      <button class="btn-insert-bib" data-position="${r.position}" title="Insert after">🔗</button>
                      <button class="btn-remove-bib" data-position="${r.position}" title="Remove">×</button>
                    </div>
                  </li>
                  ${(idx < results.length - 1 || r.hasBib) ? `
                    <li class="insert-row">
                      <button class="btn-insert-between" data-after="${r.position}" title="Insert after #${r.position}">+</button>
                    </li>
                  ` : ''}
                `
  }).join('')}

              ${results.length === 0 ? `
                <li class="result-empty">No entries yet. Enter bib numbers in finish order.</li>
              ` : ''}
            </ul>
          </div>
        </div>
      </div>
    </div>
  `

  attachChuteHandlers(container, onNavigate)
}

/**
 * Attach event handlers
 */
function attachChuteHandlers(container, onNavigate) {
  // Select race button
  document.getElementById('selectRaceBtn')?.addEventListener('click', () => {
    onNavigate('races')
  })

  // Keypad buttons
  container.querySelectorAll('.keypad-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      handleKeypadInput(btn.dataset.key, container, onNavigate)
    })
  })

  // Submit button
  document.getElementById('submitBtn')?.addEventListener('click', () => {
    handleSubmit(container, onNavigate)
  })

  // Skip button
  document.getElementById('skipBtn')?.addEventListener('click', () => {
    handleSkip(container, onNavigate)
  })

  // Unknown button
  document.getElementById('unknownBtn')?.addEventListener('click', () => {
    handleUnknown(container, onNavigate)
  })

  // Cancel button (edit/insert mode)
  document.getElementById('cancelBtn')?.addEventListener('click', () => {
    editingPosition = null
    insertAfterPosition = null
    currentBibInput = ''
    renderChutePage(container, onNavigate)
  })

  // Clear all button
  document.getElementById('clearAllBtn')?.addEventListener('click', async () => {
    const confirmed = await showConfirm({
      title: 'Clear All Entries?',
      message: 'All bib entries will be deleted. This cannot be undone.',
      confirmText: 'Clear All',
      cancelText: 'Cancel',
      type: 'danger'
    })
    if (confirmed) {
      bibEntries = []
      saveBibEntries()
      renderChutePage(container, onNavigate)
    }
  })

  // Edit buttons
  container.querySelectorAll('.btn-edit-bib').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const position = parseInt(btn.dataset.position, 10)
      startEdit(position, container, onNavigate)
    })
  })

  // Remove buttons
  container.querySelectorAll('.btn-remove-bib').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const position = parseInt(btn.dataset.position, 10)
      handleRemove(position, container, onNavigate)
    })
  })

  // Insert buttons (from list items)
  container.querySelectorAll('.btn-insert-bib').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const position = parseInt(btn.dataset.position, 10)
      startInsert(position, container, onNavigate)
    })
  })

  // Insert buttons (between rows)
  container.querySelectorAll('.btn-insert-between').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const afterPosition = parseInt(btn.dataset.after, 10)
      startInsert(afterPosition, container, onNavigate)
    })
  })

  // Keyboard handler
  if (keyboardHandler) {
    document.removeEventListener('keydown', keyboardHandler)
  }

  keyboardHandler = (e) => {
    if (!container.querySelector('.chute-page')) {
      document.removeEventListener('keydown', keyboardHandler)
      keyboardHandler = null
      return
    }

    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault()
      handleKeypadInput(e.key, container, onNavigate)
    } else if (e.key === 'Backspace') {
      e.preventDefault()
      handleKeypadInput('back', container, onNavigate)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      if (editingPosition !== null || insertAfterPosition !== null) {
        editingPosition = null
        insertAfterPosition = null
        currentBibInput = ''
        renderChutePage(container, onNavigate)
      } else {
        handleKeypadInput('clear', container, onNavigate)
      }
    } else if (e.key === 'c' || e.key === 'C') {
      e.preventDefault()
      handleKeypadInput('clear', container, onNavigate)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (currentBibInput) {
        handleSubmit(container, onNavigate)
      }
    } else if (e.key === 's' || e.key === 'S') {
      e.preventDefault()
      handleSkip(container, onNavigate)
    } else if (e.key === 'u' || e.key === 'U') {
      e.preventDefault()
      handleUnknown(container, onNavigate)
    }
  }

  document.addEventListener('keydown', keyboardHandler)
}

/**
 * Handle keypad input
 */
function handleKeypadInput(key, container, onNavigate) {
  if (key === 'clear') {
    currentBibInput = ''
  } else if (key === 'back') {
    currentBibInput = currentBibInput.slice(0, -1)
  } else if (key === 'enter') {
    // Submit the current bib
    if (currentBibInput) {
      handleSubmit(container, onNavigate)
      return // Don't update display, handleSubmit will re-render
    }
  } else if (key >= '0' && key <= '9') {
    if (currentBibInput.length < 4) {
      currentBibInput += key
    }
  }

  updateInputDisplay(container)
}

/**
 * Update input display without full re-render
 */
function updateInputDisplay(container) {
  const bibInput = document.getElementById('bibInput')
  const submitBtn = document.getElementById('submitBtn')
  const matchContainer = container.querySelector('.athlete-match')
  const hasRoster = loadRoster().length > 0

  // Check for duplicate
  const duplicateWarning = isDuplicateBib(currentBibInput)

  if (bibInput) {
    bibInput.innerHTML = currentBibInput || '<span class="placeholder">Bib #</span>'
    // Update duplicate class
    if (duplicateWarning) {
      bibInput.classList.add('duplicate')
    } else {
      bibInput.classList.remove('duplicate')
    }
  }

  // Update athlete match / duplicate warning
  if (matchContainer) {
    if (duplicateWarning) {
      matchContainer.className = 'athlete-match duplicate-warning'
      matchContainer.innerHTML = `
        <span class="warning-icon">⚠</span>
        <span class="match-name">Duplicate Bib - Already Entered</span>
      `
    } else if (currentBibInput) {
      const athlete = getAthleteByBib(parseInt(currentBibInput, 10))
      if (athlete) {
        matchContainer.className = 'athlete-match found'
        matchContainer.innerHTML = `
          <span class="match-name">${getAthleteName(athlete)}</span>
          <span class="match-team">${athlete.team || ''}</span>
        `
      } else if (hasRoster) {
        matchContainer.className = 'athlete-match not-found'
        matchContainer.innerHTML = `<span class="match-name">Not in roster</span>`
      } else {
        matchContainer.className = 'athlete-match'
        matchContainer.innerHTML = ''
      }
    } else {
      matchContainer.className = 'athlete-match'
      matchContainer.innerHTML = ''
    }
  }

  if (submitBtn) {
    submitBtn.disabled = !currentBibInput
  }

  // Also update Enter button
  const enterBtn = container.querySelector('.btn-enter')
  if (enterBtn) {
    enterBtn.disabled = !currentBibInput
  }
}

/**
 * Handle submit (add, edit, or insert)
 */
function handleSubmit(container, onNavigate) {
  if (!currentBibInput) return

  const bib = parseInt(currentBibInput, 10)
  const selectedRace = getSelectedRace()
  const entry = {
    id: generateUUID(),
    raceId: selectedRace?.id || null,
    bib,
    skipped: false,
    enteredAt: Date.now()
  }

  if (editingPosition !== null) {
    // Edit existing - preserve the id
    const existing = bibEntries[editingPosition - 1]
    entry.id = existing.id || entry.id
    entry.sequence = editingPosition
    bibEntries[editingPosition - 1] = entry
    editingPosition = null
  } else if (insertAfterPosition !== null) {
    // Insert at position
    entry.sequence = insertAfterPosition + 1
    bibEntries.splice(insertAfterPosition, 0, entry)
    // Re-sequence
    resequenceBibEntries()
    insertAfterPosition = null
  } else {
    // Add to end
    entry.sequence = bibEntries.length + 1
    bibEntries.push(entry)
  }

  saveBibEntries()
  queueForSync('bib_entry', entry)
  currentBibInput = ''

  if (navigator.vibrate) navigator.vibrate(30)

  renderChutePage(container, onNavigate)
}

/**
 * Re-sequence bib entries after insert/remove
 */
function resequenceBibEntries() {
  bibEntries.forEach((entry, idx) => {
    entry.sequence = idx + 1
  })
}

/**
 * Handle skip
 */
function handleSkip(container, onNavigate) {
  const selectedRace = getSelectedRace()
  const entry = {
    id: generateUUID(),
    raceId: selectedRace?.id || null,
    bib: null,
    skipped: true,
    enteredAt: Date.now()
  }

  if (editingPosition !== null) {
    const existing = bibEntries[editingPosition - 1]
    entry.id = existing.id || entry.id
    entry.sequence = editingPosition
    bibEntries[editingPosition - 1] = entry
    editingPosition = null
  } else if (insertAfterPosition !== null) {
    entry.sequence = insertAfterPosition + 1
    bibEntries.splice(insertAfterPosition, 0, entry)
    resequenceBibEntries()
    insertAfterPosition = null
  } else {
    entry.sequence = bibEntries.length + 1
    bibEntries.push(entry)
  }

  saveBibEntries()
  queueForSync('bib_entry', entry)
  currentBibInput = ''

  renderChutePage(container, onNavigate)
}

/**
 * Handle unknown (finisher exists but bib not known)
 */
function handleUnknown(container, onNavigate) {
  const selectedRace = getSelectedRace()
  const entry = {
    id: generateUUID(),
    raceId: selectedRace?.id || null,
    bib: null,
    unknown: true,
    enteredAt: Date.now()
  }

  if (editingPosition !== null) {
    const existing = bibEntries[editingPosition - 1]
    entry.id = existing.id || entry.id
    entry.sequence = editingPosition
    bibEntries[editingPosition - 1] = entry
    editingPosition = null
  } else if (insertAfterPosition !== null) {
    entry.sequence = insertAfterPosition + 1
    bibEntries.splice(insertAfterPosition, 0, entry)
    resequenceBibEntries()
    insertAfterPosition = null
  } else {
    entry.sequence = bibEntries.length + 1
    bibEntries.push(entry)
  }

  saveBibEntries()
  queueForSync('bib_entry', entry)
  currentBibInput = ''

  // Distinct haptic feedback for unknown (double pulse)
  if (navigator.vibrate) {
    navigator.vibrate([20, 40, 20])
  }

  renderChutePage(container, onNavigate)
}

/**
 * Start edit mode for a position
 */
function startEdit(position, container, onNavigate) {
  editingPosition = position
  insertAfterPosition = null
  const entry = bibEntries[position - 1]
  currentBibInput = entry?.bib ? String(entry.bib) : ''

  renderChutePage(container, onNavigate)
}

/**
 * Start insert mode
 */
function startInsert(afterPosition, container, onNavigate) {
  insertAfterPosition = afterPosition
  editingPosition = null
  currentBibInput = ''

  renderChutePage(container, onNavigate)
}

/**
 * Handle remove
 */
async function handleRemove(position, container, onNavigate) {
  const confirmed = await showConfirm({
    title: `Remove Entry #${position}?`,
    message: 'All entries after this will shift up by one position.',
    confirmText: 'Remove',
    cancelText: 'Cancel',
    type: 'danger'
  })
  if (confirmed) {
    bibEntries.splice(position - 1, 1)
    saveBibEntries()
    renderChutePage(container, onNavigate)
  }
}

/**
 * Get bib entries (for results page)
 */
export function getBibEntries() {
  const selectedRace = getSelectedRace()
  return loadLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace?.id)) || []
}

/**
 * Clear all bib entries for current race
 */
export function clearBibEntries() {
  const selectedRace = getSelectedRace()
  bibEntries = []
  saveLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace?.id), [])
}

/**
 * Refresh bib entries from localStorage (called after remote sync updates localStorage)
 * Preserves edit state unlike full init
 */
export function refreshBibEntriesFromStorage() {
  const selectedRace = getSelectedRace()
  bibEntries = loadLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace?.id)) || []
}

/**
 * Update a bib entry by position (called from Results page)
 * @param {number} position - 1-indexed position
 * @param {number|null} bib - New bib number
 */
export function updateBibEntry(position, bib) {
  const selectedRace = getSelectedRace()
  const entries = loadLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace?.id)) || []
  if (position > 0 && position <= entries.length) {
    entries[position - 1].bib = bib
    entries[position - 1].unknown = false
    entries[position - 1].skipped = false
    saveLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace?.id), entries)
    queueForSync('bib_entry_update', entries[position - 1])
  }
}

/**
 * Update an unknown entry with a bib (and mark not unknown)
 */
export function resolveUnknownBib(position, bib) {
  updateBibEntry(position, bib)
}

